import { AP_IMPORT_DATEI_MAX_BYTES } from '@asas/domain/ap-import-gruende';
import type { ApImportAbweisung } from '@asas/domain/ap-import-gruende';
import type {
  ApImportFormat,
  ApKlaerungGrund,
  ApKonfliktGrund,
  ApVorschau,
  Permission,
} from '@asas/domain';

import type { FeldBefund } from '../daten/fehler';

/**
 * Reine Hilfen der Importansicht für Stammarbeitspakete (AC-302): Rechte,
 * Texte zu den Gründen, Datei als base64, das Formular „Format einrichten“.
 * Nichts hier ruft den Server.
 */

/** Hochladen, Vorschau und Übernehmen (E9). */
export const AP_IMPORT_RECHT: Permission = 'work_package_library:manage';
/** Format einrichten (E10) — wie die übrige Konfiguration. */
export const AP_FORMAT_RECHT: Permission = 'tenant:configure';

export function darfApImport(rechte: ReadonlySet<Permission>): boolean {
  return rechte.has(AP_IMPORT_RECHT);
}

export function darfApFormatPflegen(rechte: ReadonlySet<Permission>): boolean {
  return rechte.has(AP_FORMAT_RECHT);
}

export const KONFLIKT_TEXTE: Readonly<Record<ApKonfliktGrund, string>> = {
  formel_ohne_wert: 'Formel ohne gespeicherten Wert — in Excel neu berechnen und speichern',
  fehlerwert: 'Fehlerwert in der Zelle (z. B. #BEZUG!)',
  bezeichnung_fehlt: 'Nr ohne Bezeichnung',
  bezeichnung_ungueltig: 'Bezeichnung zu lang oder mit Steuerzeichen',
  ohne_bereich: 'Paket vor der ersten Bereichsüberschrift',
  gewerk_unbekannt: 'Bereich ist kein bekanntes Gewerk',
  praefix_passt_nicht: 'Nr passt nicht zum Gewerk',
  nr_doppelt: 'Nr kommt im Blatt mehrfach vor',
  abweichend_in_gruppe: 'Blätter derselben Gruppe nennen Verschiedenes',
  konflikt_in_gruppe: 'Nr hat in einem Blatt derselben Gruppe (auch diesem) einen Konflikt',
  bezeichnung_doppelt: 'Zwei Pakete mit gleicher Bezeichnung im selben Gewerk',
  bezeichnung_belegt: 'Bezeichnung ist in der Bibliothek schon vergeben',
};

export const KLAERUNG_TEXTE: Readonly<Record<ApKlaerungGrund, string>> = {
  mehrere_gruppen: 'Phasen liegen in mehreren Gruppen',
  ohne_gruppe: 'Phase gehört zu keiner Gruppe',
  unlesbar: 'Phasenangabe im Blattnamen nicht lesbar',
};

export const ABWEISUNG_TEXTE: Readonly<Record<ApImportAbweisung, string>> = {
  nicht_eingerichtet: 'Für diesen Mandanten ist noch kein Importformat eingerichtet.',
  kein_xlsx: 'Die Datei ist keine Excel-Arbeitsmappe im Format .xlsx.',
  makros: 'Die Datei enthält Makros. Bitte als .xlsx ohne Makros speichern.',
  zu_gross: `Die Datei ist zu groß (höchstens ${String(
    AP_IMPORT_DATEI_MAX_BYTES / (1024 * 1024),
  )} MB) oder entpackt zu umfangreich.`,
  zu_viele_blaetter: 'Die Datei hat zu viele Blätter.',
  beschaedigt: 'Die Datei ist beschädigt oder unvollständig.',
  vorschau_veraltet:
    'Seit der Vorschau hat sich die Bibliothek oder die Konfiguration geändert; nichts wurde geschrieben. Bitte „Erneut prüfen“.',
  bereits_uebernommen: 'Dieser Import ist bereits übernommen.',
};

/** Bytes als base64, in Stücken — `String.fromCharCode(...)` verträgt keine 5 MB auf einmal. */
export function alsBase64(bytes: Uint8Array): string {
  let binaer = '';
  for (let pos = 0; pos < bytes.length; pos += 0x8000) {
    binaer += String.fromCharCode(...bytes.subarray(pos, pos + 0x8000));
  }
  return btoa(binaer);
}

export function phasenText(phasen: readonly number[]): string {
  return phasen.length === 0 ? '—' : `LPh ${phasen.join(', ')}`;
}

/** Ob eine Übernahme etwas schriebe. Ohne Änderung wird sie nicht angeboten. */
export function hatAenderungen(vorschau: ApVorschau): boolean {
  return vorschau.neu.length > 0 || vorschau.geaendert.length > 0;
}

// ---------------------------------------------------------------------------
// Formular „Format einrichten“ (E10)

export const FORMAT_FELDER = [
  'blattPraefix',
  'kopfZeilen',
  'spalteNr',
  'spalteBezeichnung',
  'nrMuster',
] as const;
export type FormatFeld = (typeof FORMAT_FELDER)[number];

/** Wie getippt: alles Text, auch die Zahl der Kopfzeilen. */
export type FormatEntwurf = Readonly<Record<FormatFeld, string>>;

export const LEERES_FORMAT: FormatEntwurf = {
  blattPraefix: '',
  kopfZeilen: '',
  spalteNr: '',
  spalteBezeichnung: '',
  nrMuster: '',
};

export function formatEntwurfAus(format: ApImportFormat | null): FormatEntwurf {
  return format === null ? LEERES_FORMAT : { ...format, kopfZeilen: String(format.kopfZeilen) };
}

/**
 * Die Eingabe für `tenant.updateSettings`. Die Kopfzeilen müssen eine ganze
 * Zahl sein; sonst `null` und ein Befund am Feld — alles Weitere prüft der Server.
 */
export function formatEingabe(
  entwurf: FormatEntwurf,
): { readonly format: ApImportFormat } | { readonly befund: FeldBefund } {
  if (!/^\d{1,3}$/.test(entwurf.kopfZeilen.trim())) {
    return { befund: { path: 'apImport.kopfZeilen', message: 'Bitte eine ganze Zahl eingeben' } };
  }

  return {
    format: {
      blattPraefix: entwurf.blattPraefix,
      kopfZeilen: Number(entwurf.kopfZeilen.trim()),
      spalteNr: entwurf.spalteNr.trim().toUpperCase(),
      spalteBezeichnung: entwurf.spalteBezeichnung.trim().toUpperCase(),
      nrMuster: entwurf.nrMuster,
    },
  };
}

/** Feldbefunde des Servers (`apImport.<feld>`) je Feld. */
export function formatMeldungen(
  felder: readonly FeldBefund[],
): Partial<Record<FormatFeld, string>> {
  const meldungen: Partial<Record<FormatFeld, string>> = {};

  for (const { path, message } of felder) {
    const feld = FORMAT_FELDER.find((name) => path === `apImport.${name}`);
    if (feld !== undefined) meldungen[feld] ??= message;
  }

  return meldungen;
}

export function istFormatGeaendert(entwurf: FormatEntwurf, basis: FormatEntwurf): boolean {
  return FORMAT_FELDER.some((feld) => entwurf[feld] !== basis[feld]);
}

/**
 * Ein neuer Stand trifft auf eigene Eingaben (apps/web/CLAUDE.md): nur Felder,
 * die die Person geändert hat, bleiben; die übrigen nehmen den neuen Stand.
 * Hat die andere Seite dasselbe Feld geändert, bleibt die eigene Eingabe, und
 * `hinweise` nennt den gespeicherten Wert.
 */
export function formatAufNeuenStand(
  entwurf: FormatEntwurf,
  basis: FormatEntwurf,
  neu: FormatEntwurf,
): { readonly entwurf: FormatEntwurf; readonly hinweise: Partial<Record<FormatFeld, string>> } {
  const ergebnis: Record<FormatFeld, string> = { ...neu };
  const hinweise: Partial<Record<FormatFeld, string>> = {};

  for (const feld of FORMAT_FELDER) {
    if (entwurf[feld] === basis[feld]) continue;
    ergebnis[feld] = entwurf[feld];
    if (neu[feld] !== basis[feld] && neu[feld] !== entwurf[feld]) {
      hinweise[feld] = `Inzwischen gespeichert: „${neu[feld]}“`;
    }
  }

  return { entwurf: ergebnis, hinweise };
}

/** Gemerkter Entwurf vor einer Neuanmeldung: eigene Eingaben und ihr Ausgangsstand. */
export interface GemerktesFormat {
  readonly entwurf: FormatEntwurf;
  readonly basis: FormatEntwurf;
}

function pruefeFormatEntwurf(roh: unknown): FormatEntwurf | null {
  if (typeof roh !== 'object' || roh === null) return null;
  const werte = roh as Record<string, unknown>;

  return FORMAT_FELDER.every((feld) => typeof werte[feld] === 'string')
    ? (Object.fromEntries(FORMAT_FELDER.map((feld) => [feld, werte[feld]])) as FormatEntwurf)
    : null;
}

/** Prüfer beim Auslesen des Entwurfsspeichers (Pflicht, `daten/entwurf.ts`). */
export function pruefeGemerktesFormat(roh: unknown): GemerktesFormat | null {
  if (typeof roh !== 'object' || roh === null) return null;
  const entwurf = pruefeFormatEntwurf((roh as Record<string, unknown>).entwurf);
  const basis = pruefeFormatEntwurf((roh as Record<string, unknown>).basis);

  return entwurf === null || basis === null ? null : { entwurf, basis };
}
